import type { SxProps, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { APP_SHAPE } from './colors';

/**
 * Sidebar nav item — subtle selected tint (not filled primary pill).
 * Primary is reserved for the active label/icon color only.
 */
export const sidebarNavItemButtonSx = (
  theme: Theme,
  { collapsed = false }: { collapsed?: boolean } = {},
): SxProps<Theme> => ({
  minHeight: 46,
  borderRadius: `${APP_SHAPE.radiusMd}px`,
  color: theme.palette.text.secondary,
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.shorter,
  }),
  ...(collapsed
    ? {
        width: 46,
        height: 46,
        p: 0,
        mx: 'auto',
        justifyContent: 'center',
      }
    : { px: 1.75, py: 1, justifyContent: 'flex-start' }),
  '& .MuiListItemIcon-root': {
    minWidth: collapsed ? 0 : 38,
    color: 'inherit',
    justifyContent: 'center',
    transition: 'inherit',
    '& .MuiSvgIcon-root': { fontSize: 22 },
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.primary.main,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: alpha(
        theme.palette.primary.main,
        theme.palette.mode === 'dark' ? 0.16 : 0.12,
      ),
      color: theme.palette.primary.main,
    },
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
  },
});

export const sidebarCollapseToggleSx = (theme: Theme): SxProps<Theme> => ({
  color: theme.palette.text.secondary,
  borderRadius: `${APP_SHAPE.radiusSm}px`,
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
});

/** Menus / popovers — flat border + light shadow (matches SectionPaper). */
export const popoverPaperSx = (theme: Theme): SxProps<Theme> => ({
  bgcolor: theme.palette.background.paper,
  border: 1,
  borderColor: 'divider',
  boxShadow: theme.shadows[1],
  borderRadius: `${APP_SHAPE.radiusMd}px`,
});

export const menuItemHoverSx: SxProps<Theme> = {
  transition: 'background-color 0.2s ease',
  '&:hover': { bgcolor: 'action.hover' },
};

export const menuItemSelectedSx = (theme: Theme): SxProps<Theme> => ({
  ...menuItemHoverSx,
  '&.Mui-selected': {
    bgcolor: theme.palette.action.selected,
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.12),
    },
  },
});
